import React, { useState, useEffect } from 'react';
import { loyaltyAPI } from '../services/api';
import { Plus, Trash2, Save, Wand2 } from 'lucide-react';
import toast from 'react-hot-toast';
import HelpHint from '../components/HelpHint';
import './Loyalty.css';
import ExpirySettings from '../components/ExpirySettings';
import {useAuth} from '../hooks/useAuth';

export default function LoyaltyPage() {
  const {user}=useAuth(); const canEditSpending=user?.role==='owner';
  const [tiers, setTiers] = useState([]);
  const [spend,setSpend]=useState(null),[savedSpend,setSavedSpend]=useState(null),[savingSpend,setSavingSpend]=useState(false);
  useEffect(()=>{loyaltyAPI.settings().then(r=>{setSpend(String(r.data.max_bonus_spend_percent));setSavedSpend(String(r.data.max_bonus_spend_percent));}).catch(()=>toast.error('Не удалось загрузить правила списания'));},[]);
  const validSpend=spend!==null&&spend!==''&&Number.isFinite(Number(spend))&&Number(spend)>=0&&Number(spend)<=100&&Math.abs(Math.round(Number(spend)*100)-Number(spend)*100)<0.000001;
  const saveSpend=async()=>{if(!validSpend)return;setSavingSpend(true);try{const r=await loyaltyAPI.saveSettings({max_bonus_spend_percent:Number(spend)});setSavedSpend(String(r.data.max_bonus_spend_percent));setSpend(String(r.data.max_bonus_spend_percent));toast.success('Правила списания сохранены');}catch(e){toast.error(e.response?.data?.detail||'Не удалось сохранить правила');}finally{setSavingSpend(false);}};
  const [showCreate, setShowCreate] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editData, setEditData] = useState({});
  const [form, setForm] = useState({
    name: '', display_name: '', min_spent: 0, cashback_percent: 5, sort_order: 0, color: '#6366f1', is_default: false,
  });

  const load = () => { loyaltyAPI.tiers().then(res => setTiers(res.data)).catch(() => {}); };
  useEffect(() => { load(); }, []);

  const handleSeedDefaults = async () => {
    try { await loyaltyAPI.seedDefaults(); toast.success('Уровни созданы'); load(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Ошибка'); }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try { await loyaltyAPI.createTier(form); toast.success('Уровень создан'); setShowCreate(false); load(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Ошибка'); }
  };

  const handleUpdate = async (id) => {
    try { await loyaltyAPI.updateTier(id, editData); toast.success('Обновлено'); setEditId(null); load(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Ошибка'); }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Удалить "${name}"?`)) return;
    try { await loyaltyAPI.deleteTier(id); toast.success('Удалено'); load(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Ошибка'); }
  };

  const startEdit = (t) => {
    setEditId(t.id);
    setEditData({ display_name: t.display_name, min_spent: t.min_spent, cashback_percent: t.cashback_percent, sort_order: t.sort_order, color: t.color, is_default: t.is_default });
  };

  return (
    <div className="animate-in">
      <div className="page-header">
        <h1 className="page-title">Программа лояльности</h1>
        <p className="page-subtitle">Начисление и использование бонусов</p>
      </div>

      <div className="loyalty-settings-grid">
      <section className="card loyalty-spending" aria-label="Правила оплаты бонусами">
        <div><h2>Оплата бонусами <HelpHint label="Как считается лимит" text="Процент от суммы покупки после скидок, а не от баланса. 1 балл = 1 ₽. Фактическое списание не может превышать доступный остаток. Запрет списания у выбранной акции или промокода имеет приоритет. Ручные корректировки баланса — отдельные операции."/></h2><p>Какую часть покупки можно оплатить баллами</p></div>
        <div className="loyalty-spending-controls"><label>Максимальная доля покупки<div className="loyalty-spending-input"><input aria-label="Максимальная оплата бонусами, %" type="number" min="0" max="100" step="0.01" value={spend??''} disabled={!canEditSpending||spend===null||savingSpend} placeholder="Загрузка…" onChange={e=>setSpend(e.target.value)}/><span>%</span></div></label><button type="button" className="btn btn-primary" disabled={!canEditSpending||!validSpend||savedSpend===spend||savingSpend} onClick={saveSpend}><Save size={15}/>{savingSpend?'Сохраняем…':'Сохранить'}</button></div>
        <div className="loyalty-spending-example">{validSpend?<>Покупка <strong>1 000 ₽</strong> · баланс <strong>1 000 баллов</strong> → можно списать до <strong>{(Math.floor(Number(spend)*10*100)/100).toLocaleString('ru-RU')} баллов</strong>.{Number(spend)===0&&' Оплата бонусами выключена.'}</>:'Укажите процент от 0 до 100.'}</div>
      </section>
      <ExpirySettings/>
      </div>
      <h2 className="loyalty-tiers-title">Уровни лояльности</h2>
      <div className="page-actions">
        <button className="btn btn-primary" onClick={() => setShowCreate(true)}><Plus size={16} /> Добавить</button>
        {tiers.length === 0 && (
          <button className="btn btn-ghost" onClick={handleSeedDefaults}><Wand2 size={16} /> Создать стандартные</button>
        )}
      </div>

      {showCreate && (
        <div className="card" style={{ marginBottom: 20 }}>
          <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 16, color: 'var(--text-secondary)' }}>Новый уровень</h3>
          <form onSubmit={handleCreate}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
              <div className="form-group">
                <label className="form-label">Системное имя</label>
                <input className="form-input" required placeholder="gold" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">Отображаемое имя</label>
                <input className="form-input" required placeholder="Золотой" value={form.display_name} onChange={e => setForm({ ...form, display_name: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">Порог (₽)</label>
                <input className="form-input" type="number" min="0" value={form.min_spent} onChange={e => setForm({ ...form, min_spent: parseFloat(e.target.value) || 0 })} />
              </div>
              <div className="form-group">
                <label className="form-label">Кешбэк %</label>
                <input className="form-input" type="number" min="0" max="100" step="0.1" value={form.cashback_percent} onChange={e => setForm({ ...form, cashback_percent: parseFloat(e.target.value) || 0 })} />
              </div>
              <div className="form-group">
                <label className="form-label">Порядок</label>
                <input className="form-input" type="number" value={form.sort_order} onChange={e => setForm({ ...form, sort_order: parseInt(e.target.value) || 0 })} />
              </div>
              <div className="form-group">
                <label className="form-label">Цвет</label>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input type="color" value={form.color} onChange={e => setForm({ ...form, color: e.target.value })}
                    style={{ width: 40, height: 36, border: 'none', borderRadius: 4, cursor: 'pointer', background: 'transparent' }} />
                  <input className="form-input" value={form.color} onChange={e => setForm({ ...form, color: e.target.value })}
                    style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }} />
                </div>
              </div>
            </div>
            <label className="form-check" style={{ marginTop: 8 }}>
              <input type="checkbox" checked={form.is_default} onChange={e => setForm({ ...form, is_default: e.target.checked })} />
              По умолчанию для новых клиентов
            </label>
            <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
              <button type="submit" className="btn btn-primary">Создать</button>
              <button type="button" className="btn btn-ghost" onClick={() => setShowCreate(false)}>Отмена</button>
            </div>
          </form>
        </div>
      )}

      <div className="table-container">
        <table className="table">
          <thead>
            <tr>
              <th>Цвет</th><th>Имя</th><th>Отображение</th><th>Порог (₽)</th><th>Кешбэк</th><th>Порядок</th><th>По умолч.</th><th>Действия</th>
            </tr>
          </thead>
          <tbody>
            {tiers.map(t => (
              <tr key={t.id}>
                <td>
                  {editId === t.id ? (
                    <input type="color" value={editData.color} onChange={e => setEditData({ ...editData, color: e.target.value })}
                      style={{ width: 32, height: 28, border: 'none', borderRadius: 4, cursor: 'pointer', background: 'transparent' }} />
                  ) : (
                    <div style={{ width: 24, height: 24, borderRadius: '50%', background: t.color, border: '2px solid var(--border)' }} />
                  )}
                </td>
                <td style={{ fontFamily: 'var(--font-mono)' }}>{t.name}</td>
                <td>
                  {editId === t.id
                    ? <input className="form-input" style={{ padding: '4px 8px', fontSize: 13 }} value={editData.display_name} onChange={e => setEditData({ ...editData, display_name: e.target.value })} />
                    : <span style={{ color: t.color, fontWeight: 600 }}>{t.display_name}</span>}
                </td>
                <td style={{ fontFamily: 'var(--font-mono)' }}>
                  {editId === t.id
                    ? <input className="form-input" type="number" style={{ padding: '4px 8px', width: 100, fontSize: 13 }} value={editData.min_spent} onChange={e => setEditData({ ...editData, min_spent: parseFloat(e.target.value) || 0 })} />
                    : t.min_spent.toLocaleString('ru')}
                </td>
                <td style={{ fontFamily: 'var(--font-mono)' }}>
                  {editId === t.id
                    ? <input className="form-input" type="number" step="0.1" style={{ padding: '4px 8px', width: 80, fontSize: 13 }} value={editData.cashback_percent} onChange={e => setEditData({ ...editData, cashback_percent: parseFloat(e.target.value) || 0 })} />
                    : `${t.cashback_percent}%`}
                </td>
                <td style={{ fontFamily: 'var(--font-mono)' }}>
                  {editId === t.id
                    ? <input className="form-input" type="number" style={{ padding: '4px 8px', width: 60, fontSize: 13 }} value={editData.sort_order} onChange={e => setEditData({ ...editData, sort_order: parseInt(e.target.value) || 0 })} />
                    : t.sort_order}
                </td>
                <td>{t.is_default ? <span className="badge badge-success">Да</span> : '—'}</td>
                <td>
                  <div style={{ display: 'flex', gap: 6 }}>
                    {editId === t.id ? (
                      <>
                        <button className="btn btn-success btn-sm" onClick={() => handleUpdate(t.id)}><Save size={14} /></button>
                        <button className="btn btn-ghost btn-sm" onClick={() => setEditId(null)}>Отмена</button>
                      </>
                    ) : (
                      <>
                        <button className="btn btn-ghost btn-sm" onClick={() => startEdit(t)}>Изменить</button>
                        <button className="btn btn-danger btn-sm" onClick={() => handleDelete(t.id, t.display_name)}><Trash2 size={14} /></button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {tiers.length === 0 && (
              <tr><td colSpan={8} style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>
                Уровни не настроены
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}